import { AlertTriangle, Info } from 'lucide-react';
import type { CSSProperties, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './button';
import { cx } from './cx';
import { Icon } from './icon';

// The three non-data states every list and card has (07 §1.2, S30 «Stati
// non-dato»): empty says what to do, loading is a skeleton (never a spinner),
// error says what failed and offers Retry.

export function Skeleton({
  width,
  height = 10,
  round,
  className,
}: {
  width?: CSSProperties['width'];
  height?: CSSProperties['height'];
  round?: boolean;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx('skel', className)}
      style={{ width, height, ...(round ? { borderRadius: 999 } : {}) }}
    />
  );
}

/** Skeleton rows for a table (`columns` header labels are kept, the cells pulse). */
export function SkeletonTable({ columns, rows = 2 }: { columns: string[]; rows?: number }) {
  const { t } = useTranslation();
  const widths = [
    ['80%', '60%', '50%', '40%'],
    ['70%', '55%', '60%', '45%'],
  ];
  return (
    <div className="card" style={{ overflow: 'hidden' }} role="status" aria-label={t('states.loading')}>
      <div className="trow h">
        {columns.map((c) => (
          <span key={c}>{c}</span>
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: static placeholder rows
        <div className="trow" key={r}>
          {columns.map((c, i) => (
            <Skeleton
              key={c}
              height={i === 1 ? 14 : 10}
              round={i === 1}
              width={widths[r % 2]?.[i % 4] ?? '60%'}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Skeleton for a card: a title bar and two lines. */
export function SkeletonCard() {
  const { t } = useTranslation();
  return (
    <div
      className="card"
      style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 8 }}
      role="status"
      aria-label={t('states.loading')}
    >
      <Skeleton height={12} width="40%" />
      <Skeleton height={10} width="90%" />
      <Skeleton height={10} width="70%" />
    </div>
  );
}

/** Empty list: a title, what it means, and the way out. */
export function EmptyState({
  title,
  body,
  action,
}: {
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="card" style={{ padding: 18, textAlign: 'center' }}>
      <strong>{title}</strong>
      {body ? <div className="m2">{body}</div> : null}
      {action ? <div style={{ marginTop: 8 }}>{action}</div> : null}
    </div>
  );
}

/** Failed load: what failed, and Retry. */
export function ErrorState({
  title,
  body,
  onRetry,
}: {
  title?: ReactNode;
  body?: ReactNode;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="card" style={{ padding: 18, textAlign: 'center' }} role="alert">
      <strong className="c-bl">{title ?? t('states.errorTitle')}</strong>
      {body ? <div className="m2">{body}</div> : null}
      {onRetry ? (
        <Button size="sm" variant="primary" style={{ marginTop: 8 }} onClick={onRetry}>
          {t('actions.retry')}
        </Button>
      ) : null}
    </div>
  );
}

/** A card that has no data or failed on its own (S30 «Stati non-dato · card»): the rest of the page works. */
export function CardNotice({
  tone = 'info',
  title,
  body,
  onRetry,
}: {
  tone?: 'info' | 'warning';
  title: ReactNode;
  body?: ReactNode;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      className="card"
      style={{ padding: 14, display: 'flex', gap: 10 }}
      role={tone === 'warning' ? 'alert' : undefined}
    >
      <Icon as={tone === 'warning' ? AlertTriangle : Info} className={tone === 'warning' ? 'c-wa' : 'c-pl'} />
      <div>
        <strong>{title}</strong>
        {body ? <div className="m2">{body}</div> : null}
        {onRetry ? (
          <Button size="sm" style={{ marginTop: 6 }} onClick={onRetry}>
            {t('actions.retry')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
